import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import HexInput from "@/components/ui/hex-input";
import { ColorPicker } from "@/components/ui/color-picker";
import { Eye, EyeOff, Pipette } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

/** Card-like container for every settings sub-section. */
export function Section({ title, subtitle, action, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6 md:p-8">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-medium text-[#1a1a1a]">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </header>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

/** Primary save CTA, auto-mutes when there are no unsaved changes. */
export function SaveButton({ onClick, testid, label, disabled, dirty = true }) {
  const inactive = disabled || !dirty;
  return (
    <Button
      onClick={onClick}
      disabled={inactive}
      data-testid={testid}
      className={`h-10 rounded-lg shrink-0 px-4 transition-colors ${
        inactive
          ? "bg-gray-100 text-gray-400 hover:bg-gray-100 cursor-not-allowed"
          : "bg-slate-900 text-white hover:bg-slate-800"
      }`}
    >
      {label}
    </Button>
  );
}

/** Label + swatch picker + hex input. */
export function ColorField({ label, color, onChange, testid }) {
  const hexValue = (color || "").replace("#", "");
  return (
    <div className="space-y-2">
      <div className="text-[14px] font-normal text-[#1a1a1a]">{label}</div>
      <ColorPicker
        value={color}
        onChange={onChange}
        trigger={
          <button
            type="button"
            data-testid={`${testid}-swatch`}
            className="relative block w-full h-24 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/30 transition-shadow overflow-hidden"
            style={{ background: color || "#eee" }}
            aria-label={`${label} color picker`}
          >
            <div className="absolute bottom-2 right-2 w-7 h-7 rounded-md bg-white/95 shadow-sm flex items-center justify-center pointer-events-none">
              <Pipette className="w-3.5 h-3.5 text-[#1a1a1a]" strokeWidth={1.75} />
            </div>
          </button>
        }
      />
      <HexInput
        value={hexValue}
        onChange={(v) => onChange(v ? `#${v}` : "")}
        testid={`${testid}-input`}
      />
    </div>
  );
}

/** Password input with eye-toggle, used in profile change-password flow. */
export function PasswordField({ label, value, onChange, show, onToggle, testid }) {
  const { t } = useI18n();
  return (
    <div>
      <Label className="text-[13px] text-[#1a1a1a]">{label}</Label>
      <div className="relative mt-1">
        <Input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
          className="h-11 bg-white border-gray-200 pr-10"
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={onToggle}
          className="absolute inset-y-0 right-2 flex items-center justify-center w-7 text-gray-400 hover:text-[#1a1a1a]"
          aria-label={show ? t("settings.hide_password") : t("settings.show_password")}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
